import { supabase } from '../supabaseClient';
import { normaliseProjectRef, projectRefKey } from './projectDebriefs';

// Start a debrief for a BRIEF, or return the one that already exists for it.
//
// Two places do this — the debriefs index and the "start the debrief" prompt on
// a brief's feedback card — and the insert has to be identical in both.
//
// KEYED ON brief_id (2026-08-14). It used to key on the FS number, on the
// principle that one project deserves one write-up however many suppliers it
// involved. That collided in practice: two briefs carrying the same FS number
// for different clients shared a debrief, so opening it from one brief landed
// on the other one's. Rob's call is one debrief per brief.
//
// project_ref / project_name / client are still COPIED onto the row rather than
// joined, because a brief can be renamed or archived and the debrief still has
// to read correctly years later — and resurfacing matches past actions on
// client, which stays a project-level question.
//
// Returns { id, created, error }.
export async function startOrOpenDebrief({ briefId, projectRef, projectName, client, writtenBy }) {
  if (!briefId) {
    return { id: null, created: false, error: 'This debrief has no brief to attach to.' };
  }
  const ref = normaliseProjectRef(projectRef);

  // Never create a second debrief for the same brief. The unique index on
  // brief_id is the real guard; this read is so the common case returns the
  // existing one instead of surfacing a constraint error.
  const { data: existing, error: readErr } = await supabase
    .from('project_debriefs')
    .select('id')
    .eq('brief_id', briefId)
    .maybeSingle();
  if (readErr) return { id: null, created: false, error: readErr.message };
  if (existing) return { id: existing.id, created: false, error: null };

  const { data, error } = await supabase.from('project_debriefs').insert([{
    brief_id: briefId,
    project_ref: ref || null,
    project_name: projectName || null,
    client: client || null,
    debrief_date: new Date().toISOString().slice(0, 10),
    written_by: writtenBy || null,
    status: 'draft',
  }]).select('id').single();
  if (error) {
    // Lost a race against another tab or a second click — the row now exists,
    // so return it rather than reporting a failure the user can't act on.
    const { data: raced } = await supabase
      .from('project_debriefs').select('id').eq('brief_id', briefId).maybeSingle();
    if (raced) return { id: raced.id, created: false, error: null };
    return { id: null, created: false, error: error.message };
  }
  return { id: data.id, created: true, error: null };
}

// Legacy rows written before brief_id existed carry only an FS number. Used as
// a FALLBACK so those debriefs stay reachable from their brief — never to
// create, and never in preference to a brief_id match.
export function legacyDebriefForRef(debriefs = [], projectRef) {
  const key = projectRefKey(projectRef);
  if (!key) return null;
  return (debriefs || []).find(d => !d.brief_id && projectRefKey(d.project_ref) === key) || null;
}
